import React, { useEffect, useState } from "react";
import axios from "axios";
import { base_url } from "../../context/Authcontext";

/* =====================================================================
   Sign-in help  (admin-assisted account recovery)
   =====================================================================
   The old "Forgot Password" modal asked for the OLD password, so it was
   useless to anyone who had actually forgotten it. This site has no SMS
   or email service wired up, so the secure, no-new-infrastructure path is
   admin-assisted: the player contacts support, and an admin resets the
   password (or reads back the User ID) from the panel.

   One panel serves both cases -- `mode` is "password" or "userid" -- and
   it pulls the live support contacts (WhatsApp / Telegram) from the same
   public endpoint the floating support button already uses. If none are
   configured it falls back to a plain instruction rather than dead
   buttons.
   ===================================================================== */

const COPY = {
  password: {
    title: "Reset your password",
    lead: "For your security, passwords are reset by our support team.",
    steps: [
      "Contact support on WhatsApp or Telegram below.",
      "Tell them your User ID (or your registered mobile number).",
      "They will verify you and set a new password you can log in with.",
    ],
  },
  userid: {
    title: "Recover your User ID",
    lead: "Our support team can look up the User ID linked to your account.",
    steps: [
      "Contact support on WhatsApp or Telegram below.",
      "Give them your registered mobile number.",
      "They will confirm your identity and share your User ID.",
    ],
  },
};

const SignInHelp = ({ open, mode = "password", onClose }) => {
  const [contacts, setContacts] = useState(null); // null = loading, {} = loaded

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${base_url}/admin/webapi/get/telegramData`, {
          headers: { "content-type": "application/json" },
        });
        const row = res.data?.result?.[0] || {};
        if (!cancelled) setContacts(row);
      } catch {
        if (!cancelled) setContacts({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const c = COPY[mode] || COPY.password;
  const waNumber = contacts?.whatsappnumber
    ? String(contacts.whatsappnumber).replace(/[^0-9]/g, "")
    : "";
  const teleLink =
    contacts?.teleservice === "yes" && contacts?.telelink ? contacts.telelink : "";
  const hasContacts = waNumber || teleLink;

  const waMessage = encodeURIComponent(
    mode === "password"
      ? "Hi, I forgot my password and need help resetting my account."
      : "Hi, I forgot my User ID and need help recovering it.",
  );

  return (
    <div className="rb-help" role="dialog" aria-modal="true" aria-label={c.title}>
      <div className="rb-help__backdrop" onClick={onClose} />
      <div className="rb-help__card" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="rb-help__close" aria-label="Close" onClick={onClose}>
          &times;
        </button>

        <h2 className="rb-help__title">{c.title}</h2>
        <p className="rb-help__lead">{c.lead}</p>

        <ol className="rb-help__steps">
          {c.steps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>

        {contacts === null ? (
          <p className="rb-help__muted">Loading support contacts…</p>
        ) : hasContacts ? (
          <div className="rb-help__actions">
            {waNumber ? (
              <a
                className="rb-help__btn rb-help__btn--wa"
                href={`https://wa.me/${waNumber}?text=${waMessage}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa fa-whatsapp" aria-hidden="true" /> Chat on WhatsApp
              </a>
            ) : null}
            {teleLink ? (
              <a
                className="rb-help__btn rb-help__btn--tg"
                href={teleLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa fa-telegram" aria-hidden="true" /> Message on Telegram
              </a>
            ) : null}
          </div>
        ) : (
          <p className="rb-help__muted">
            Please reach out to our support team and an admin will help you
            {mode === "password" ? " reset your password." : " recover your User ID."}
          </p>
        )}

        <button type="button" className="rb-help__dismiss" onClick={onClose}>
          Back to login
        </button>
      </div>

      <style>{`
        .rb-help {
          position: fixed; inset: 0; z-index: 20000;
          display: flex; align-items: center; justify-content: center;
          padding: 16px;
        }
        .rb-help__backdrop {
          position: absolute; inset: 0;
          background: rgba(4, 10, 20, 0.72);
        }
        .rb-help__card {
          position: relative; z-index: 1;
          width: 100%; max-width: 380px;
          background: #0d2035;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 14px;
          padding: 24px 22px 20px;
          box-shadow: 0 12px 40px rgba(0,0,0,0.5);
          color: #f5f7fa;
          font-family: "Inter", -apple-system, "Segoe UI", Roboto, sans-serif;
        }
        .rb-help__close {
          position: absolute; top: 10px; right: 12px;
          background: none; border: 0; color: #91a4b8;
          font-size: 26px; line-height: 1; cursor: pointer;
        }
        .rb-help__close:hover { color: #f5f7fa; }
        .rb-help__title {
          margin: 0 0 6px; font-size: 19px; font-weight: 700;
          letter-spacing: -0.01em; color: #f5f7fa;
        }
        .rb-help__lead { margin: 0 0 14px; font-size: 13px; color: #91a4b8; line-height: 1.5; }
        .rb-help__steps {
          margin: 0 0 18px; padding-left: 18px;
          display: flex; flex-direction: column; gap: 8px;
          font-size: 13.5px; line-height: 1.45; color: #d7e0ea;
        }
        .rb-help__actions { display: flex; flex-direction: column; gap: 10px; }
        .rb-help__btn {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          min-height: 44px; border-radius: 10px; text-decoration: none;
          font-size: 14px; font-weight: 600; color: #071525;
        }
        .rb-help__btn--wa { background: #25d366; }
        .rb-help__btn--tg { background: #2aabee; color: #fff; }
        .rb-help__btn:hover { filter: brightness(1.05); }
        .rb-help__muted {
          margin: 0; padding: 12px 14px; border-radius: 10px;
          background: #122a43; border: 1px solid rgba(255,255,255,0.08);
          font-size: 13px; color: #91a4b8; line-height: 1.5;
        }
        .rb-help__dismiss {
          width: 100%; margin-top: 16px; min-height: 42px;
          background: transparent; border: 1px solid rgba(255,255,255,0.16);
          border-radius: 10px; color: #f5f7fa; font-size: 14px; font-weight: 600;
          cursor: pointer;
        }
        .rb-help__dismiss:hover { background: #122a43; }
      `}</style>
    </div>
  );
};

export default SignInHelp;
